import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  darken,
  Fab,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CustomDataGrid from '@/components/CustomDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import PencilOutlinedIcon from '@/components/icons/PencilOutlinedIcon';
import { useDeleteConfiguration } from '@/hooks/admin/useConfigurations';
import TableDeleteActionWithConfirmation from '@/components/TableDeleteActionWithConfirmation';
import { isDateExpired, isDateExpiring } from '@/lib/dates';
import { ConfigurationUploadStatus } from '@/components/ConfigurationUploadStatus';
import { useConfigurationsUploadStatus } from '@/hooks/admin/useConfigurationUploadStatus';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function ConfigurationsTable({
  configurations,
}: {
  configurations: any[];
}) {
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const deleteConfiguration = useDeleteConfiguration();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const configurationIds = configurations.map((config) => config._id);
  const { data: uploadStatusMap = {} } =
    useConfigurationsUploadStatus(configurationIds);

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);

    try {
      // Invalidate both configurations and upload status caches
      await Promise.all([
        // queryClient.invalidateQueries({
        //   queryKey: ['configurations'],
        //   exact: false,
        // }),
        queryClient.invalidateQueries({
          queryKey: ['configurations-upload-status'],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['configuration-upload-status'],
          exact: false,
        }),
      ]);

      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Error refreshing status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // useEffect(() => {
  //   console.log('Upload status map updated:', uploadStatusMap);
  // }, [uploadStatusMap]);

  useEffect(() => {
    const hasCompletedUploads = Object.values(uploadStatusMap).some(
      (status: any) =>
        status?.latestStatus?.status === 'completed' ||
        status?.latestStatus?.status === 'failed',
    );

    if (hasCompletedUploads) {
      queryClient.invalidateQueries({
        queryKey: ['configurations'],
        exact: false,
      });
    }
  }, [uploadStatusMap, queryClient]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 400,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="start"
            alignItems="center"
            height="100%"
          >
            <Typography sx={{ fontWeight: '700', fontSize: '14px' }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'permitsTotal',
      headerName: 'Permits Total',
      headerAlign: 'center',
      minWidth: 250,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            {params.row.permits.length}
          </Box>
        );
      },
    },
    {
      field: 'overduePermits',
      headerName: 'Overdue',
      headerAlign: 'center',
      minWidth: 250,
      // valueGetter: (_, row) => {
      //   return row.permits.reduce((acc: number, curr: any) => {
      //     const currentDate = new Date(Date.now());
      //     const expiryDate = new Date(curr.expiryDate);
      //     return expiryDate < currentDate ? acc + 1 : acc;
      //   }, 0);
      // },
      renderCell: (params) => {
        const count = params.row.permits.reduce((acc: number, curr: any) => {
          const expiryDate = new Date(curr.expiryDate);
          return isDateExpired(expiryDate) ? acc + 1 : acc;
        }, 0);
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{
              backgroundColor: count > 0 ? '#8a3836' : 'transparent',
            }}
          >
            {count}
          </Box>
        );
      },
    },
    {
      field: 'upcomingPermits',
      headerName: 'Upcoming',
      headerAlign: 'center',
      minWidth: 250,
      // valueGetter: (_, row) => {
      //   return row.permits.reduce((acc: number, curr: any) => {
      //     const currentDate = new Date(Date.now());
      //     const expiryDate = new Date(curr.expiryDate);
      //     return expiryDate < currentDate ? acc + 1 : acc;
      //   }, 0);
      // },
      renderCell: (params) => {
        const count = params.row.permits.reduce((acc: number, curr: any) => {
          const expiryDate = new Date(curr.expiryDate);
          return isDateExpiring(expiryDate) ? acc + 1 : acc;
        }, 0);
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{
              backgroundColor: count > 0 ? '#906a2f' : 'transparent',
            }}
          >
            {count}
          </Box>
        );
      },
    },
    {
      field: 'uploadStatus',
      headerName: 'Uploads Status',
      headerAlign: 'center',
      minWidth: 200,
      renderCell: (params) => {
        const configId = params.row._id;
        const statusData = uploadStatusMap[configId];

        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            gap={1}
          >
            <ConfigurationUploadStatus
              configurationId={configId}
              uploadStatuses={params.row.uploadStatuses}
              isProcessing={statusData?.isProcessing || params.row.isProcessing}
              detailedStatus={statusData?.detailedStatus}
              freshStatusData={statusData}
            />

            <Tooltip title="Refresh status">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefreshStatus();
                }}
                disabled={isRefreshing}
                sx={{
                  opacity: 0.7,
                  '&:hover': { opacity: 1 },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'edit',
      headerName: 'Actions',
      headerAlign: 'center',
      minWidth: 150,
      flex: 1,
      renderCell: (params: any) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Fab
            color="primary"
            size="small"
            aria-label="add"
            sx={{
              mr: 2,
              '&:hover': {
                backgroundColor: darken('#F6AE2D', 0.2),
              },
            }}
            onClick={() => {
              navigate(`/admin/configurations/edit/${params.row._id}`);
            }}
          >
            <PencilOutlinedIcon />
          </Fab>
          <TableDeleteActionWithConfirmation
            title="Delete Configuration"
            message={
              <>
                <Typography>
                  This vehicle will no longer be visible by all associated
                  members.
                </Typography>
                <Typography>
                  Do you still wish to permanently delete this vehicle?
                </Typography>
              </>
            }
            handleDeleteClick={() => deleteConfiguration.mutate(params.row._id)}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box
        pb={6}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6" sx={{ color: '#EAEAEA' }}>
          Configurations
        </Typography>
        <Button
          variant="contained"
          size="small"
          color="primary"
          startIcon={<AddOutlinedIcon />}
          onClick={() => {
            navigate(`/admin/vehicles/${vehicleId}/configurations/new`);
          }}
          sx={{ textTransform: 'uppercase', fontWeight: '700' }}
        >
          Add New
        </Button>
      </Box>
      {configurations && configurations.length > 0 && (
        <CustomDataGrid
          getRowId={(row) => row._id}
          rows={configurations.filter((config) => config && config.vehicle)}
          columns={columns}
          filter={false}
          search={false}
        />
      )}
    </Box>
  );
}
